import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Field from './Field.styled';
import Radio, { RadioItemEditorContainer, RadioGroupFieldset } from './FormRadioGroup.styled';
import { FormLegend, RadioCheckboxLegendLabel } from './Form.styled';
import { SROnly } from '../../GlobalStyle/utilities/utilities.styled';
import { getID } from '../../utils';
import { trackMe } from '../ComponentTracker/componentTracker';

export const FormRadioGroup = ({
    id,
    name,
    className,
    legend,
    helpMessage,
    fieldType,
    options,
    value,
    hasError,
    errorMessage,
    vertical,
    onChange,
    inputRef,
    ...rest
}) => {

    useEffect(() => {
        trackMe('FormRadioGroup');
    }, []);

    const fieldClass = className === undefined ? '' : ' ' + className;

    const genID = () => {
        return id || getID();
    };

    const elemID = genID();

    return (
        <RadioGroupFieldset
            inline={ !vertical }
            aria-invalid={ hasError }
            className={ fieldClass }
        >
            {legend && <FormLegend>
                <RadioCheckboxLegendLabel>{legend}</RadioCheckboxLegendLabel>
                {helpMessage && (
                    <Field.GroupHelp>{helpMessage}</Field.GroupHelp>
                )}
                {hasError && errorMessage && <SROnly>{errorMessage}</SROnly>}
            </FormLegend>}

            <Radio>
                {options.map((option, index) =>
                    <RadioItem
                        key={ `${elemID}-${index}` }
                        id={ `${elemID}-${index}` }
                        option={ option }
                        value={ value }
                        fieldType={ fieldType }
                        onSelection={ onChange }
                        hasError={ hasError }
                        name={ name }
                        inputRef={ inputRef }
                    />
                )}
            </Radio>
            {hasError && <Field.Error aria-hidden='true'>{errorMessage}</Field.Error>}
        </RadioGroupFieldset>
    );
};

FormRadioGroup.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    className: PropTypes.string,
    legend: PropTypes.string.isRequired,
    helpMessage: PropTypes.string,
    fieldType: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.any.isRequired,
            label: PropTypes.node.isRequired,
        })
    ).isRequired,
    value: PropTypes.any,
    hasError: PropTypes.bool,
    errorMessage: PropTypes.string,
    vertical: PropTypes.bool,
    onChange: PropTypes.func,
    inputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object,
    ]),
};

export const RadioItem = ({ id, name, option, value, fieldType, onSelection, hasError, inputRef }) => {
    const handleClick = e => {
        if (e.target.tagName === 'INPUT') {
            e.target.checked = true;

            let selectedValue = e.target.value;
            if (fieldType === 'boolean') {
                selectedValue = stringToBoolean(selectedValue);
            }
            onSelection(selectedValue);
        }
    };

    const stringToBoolean = b => b.toLowerCase() === 'true';

    const checked = value === undefined ? value : option.value === value;
    const editor = checked && option.editor ? option.editor : undefined;

    return (
        <>
            <Radio.Item onClick={ handleClick }>
                <Radio.Input
                    className={ hasError ? 'error' : '' }
                    name={ name }
                    type='radio'
                    id={ id }
                    value={ option.value }
                    checked={ checked }
                    ref={ inputRef }
                    readOnly
                />
                <Radio.Label htmlFor={ id } hasError={ hasError }>{option.label}</Radio.Label>
            </Radio.Item>
            {editor && (
                <RadioItemEditorContainer>
                    {editor}
                </RadioItemEditorContainer>
            )}
        </>);
};

RadioItem.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    hasError: PropTypes.bool,
    option: PropTypes.shape({
        editor: PropTypes.node
    }),
    value: PropTypes.any,
    fieldType: PropTypes.string,
    onSelection: PropTypes.func,
    inputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object,
    ]),
};

export default FormRadioGroup;
